import { Suspense, useContext, useState, useEffect } from 'react'
import Section from './_Section'
import createGradientElement from '../../helpers/markup'
import {
  addBgImageClass,
  lightOrDark,
  normalizeHex,
  transformPaddingToTailwind,
  getSectionTheme,
} from '../../helpers/style'
import ButtonEl from '../helpers/ctas/Button'
import { ArrowRightIcon, PlayIcon, XMarkIcon, LockClosedIcon } from '@heroicons/react/24/solid'
import HeroTitle from '../helpers/CardListingHeroTitle'
import Modal from 'react-modal'
import Loader from '../helpers/Loader'
import Picture from '../helpers/Picture'
import { MarkupContext } from '../../providers/Markup'
import MetaBadge from '../helpers/MetaBadge'
import FeaturedListCuratedCard from './FeaturedListCuratedCard'
import LockedContentBanner from './LockedContentBanner'
import CuratedCardTitle from '../helpers/CuratedCardTitle'
import { dataLayerPush } from '../../helpers/thirdparty'
import { resolveCTALabel } from '../../helpers/ctaLabel'
Modal.setAppElement('#root')

const stripHtml = (html) => (html ? html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() : '')

const hasContent = (str) =>
  !!(
    str &&
    str
      .replace(/(<([^>]+)>)/gi, '')
      .replace(/&nbsp;/gi, '')
      .trim()
  )


async function getYTEmbed(url) {
  try {
    const response = await fetch(`https://www.youtube.com/oembed?url=${url}&format=json`)
    const responseJson = await response.json()
    return responseJson
  } catch (error) {
    console.error(error)
  }
}

const CardListBGImage = (props) => {
  const { createMarkup } = useContext(MarkupContext)
  const { cardItem, i, isMember = null, onLockedClick, ...otherProps } = props
  const [modalIsOpen, setIsOpen] = useState(false)
  const [videoEmbed, setVideoEmbed] = useState(null)
  const [gradientColor, setGradientColor] = useState('#fff')

  const badgeData = cardItem?.metaBadgeData || {}
  const firstCategoryTag = badgeData?.categoryTags?.[0]
  const isVisuallyLocked = !!badgeData.isLocked && isMember === false

  const handleCardClick = (e) => {
    if (isVisuallyLocked) {
      e.preventDefault()
      dataLayerPush({ event: 'login_intent', click_text: stripHtml(cardItem.title), member_only: true }, e.currentTarget)
      onLockedClick?.()
      return
    }
    dataLayerPush({
      event: 'article_click',
      click_text: stripHtml(cardItem.title),
      category: badgeData.categoryTags?.length ? badgeData.categoryTags.join(', ') : undefined,
      member_only: false,
    }, e.currentTarget)
  }

  useEffect(() => {
    const fgColor = props.imageGradientColour?.replace('#', '') || ''
    setGradientColor((prevState) => (fgColor ? `#${fgColor}` : prevState))
  }, [])

  function openModal() {
    setIsOpen(true)
  }

  async function afterOpenModal() {
    const embedCode = getYTEmbed(cardItem.videoUrl)
    embedCode.then((res) => {
      if (!res.error) {
        setVideoEmbed(res)
      }
    })
  }

  function closeModal() {
    setIsOpen(false)
  }

  let backgroundColor = normalizeHex(props.cardBackgroundColour)
  const lightOrDarkValue = lightOrDark(backgroundColor || normalizeHex(props.backgroundColor) || 'transparent')

  const isStandardLayout = otherProps.layoutVariant === null || otherProps.layoutVariant === 'standard'

  const gridClasses = isStandardLayout
    ? `lg:nth-[3n+3]:col-start-9
        lg:nth-[3n+2]:col-start-5
        lg:nth-[3n+1]:col-start-1
        lg:[&:last-child:first-child]:col-start-5
        lg:[&:last-child:nth-child(3n+1)]:col-start-5
        lg:[&:nth-last-child(2):first-child]:col-start-3
        lg:[&:nth-last-child(2):nth-child(3n+1)]:col-start-3
        lg:[&:nth-child(2):last-child]:col-start-7
        lg:[&:last-child:nth-child(3n+2)]:col-start-7
        rounded-e-3xl rounded-bl-3xl
        max-md:col-span-full md:col-span-6 lg:col-span-4`
    : `max-md:col-span-full ${i === 0 ? 'md:col-span-full lg:col-span-6' : i <= 2 ? 'md:col-span-6 lg:col-span-3' : 'md:col-span-6 lg:col-span-4'}`

  return (
    <div
      key={i}
      data-bg={getSectionTheme(backgroundColor)}
      style={{
        '--mobile-bg-url': cardItem.mobileImage ? `url(${cardItem.mobileImage.src})` : null,
        '--desktop-bg-url': cardItem.desktopImage ? `url(${cardItem.desktopImage.src})` : null,
        '--bg-color': backgroundColor || 'transparent',
        '--light-or-dark': lightOrDarkValue,
        '--text-color':
          lightOrDarkValue === 'dark' || cardItem.desktopImage || cardItem.mobileImage
            ? 'var(--color-light)'
            : 'var(--color-dark)',
      }}
      // FIX: Upgraded grid classes to 12-column math (spanning 4 columns each)
      className={`relative w-full pt-4 px-8 pb-8 ${gridClasses}
         bg-(--bg-color) text-(--text-color) ${lightOrDarkValue === 'light' ? 'border-1 border-(--color-border-tertiary)' : ''} h-full overflow-hidden ${addBgImageClass(cardItem.desktopImage?.src, cardItem.mobileImage?.src)}`}
    >
      {props.imageGradientColour &&
        createGradientElement({
          type: props.imageGradientType,
          fromColor: gradientColor,
        })}
      <div
        className={`relative min-h-96 h-full mt-auto flex flex-col ${cardItem.videoUrl ? 'justify-between' : 'justify-end'}`}
      >
        {cardItem.videoUrl ? (
          <ButtonEl className="h-20 w-20 mx-auto mt-20" onClick={openModal} theme="none">
            <PlayIcon role="none" className="text-current" />
            <span className="sr-only">Play video</span>
          </ButtonEl>
        ) : null}
        <div>
          {(firstCategoryTag || badgeData?.readTime || badgeData?.publicationDate) && (
            <div className="flex flex-row items-center w-full mb-4">
              <div className="flex-1">
                {(firstCategoryTag || badgeData?.readTime) && (
                  <MetaBadge
                    primaryText={firstCategoryTag}
                    secondaryText={badgeData?.readTime}
                    isLocked={!!badgeData?.isLocked}
                    icon={badgeData?.isLocked ? <LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" /> : null}
                    tagType={!!badgeData?.isLocked ? 'locked_news_articles' : 'unlocked_news_articles'}
                  />
                )}
              </div>
              {badgeData?.publicationDate && (
                <span className="text-xs font-normal shrink-0">{badgeData?.publicationDate}</span>
              )}
            </div>
          )}
          {cardItem.title && (
            <CuratedCardTitle
              title={cardItem.title}
              useSmallTitle={cardItem.useSmallTitle}
              titleAlignment={cardItem.titleAlignment}
              className="block"
            />
          )}
          {hasContent(cardItem.description) && (
            <div
              className="mt-4 md:mt-8"
              data-rte="true"
              dangerouslySetInnerHTML={createMarkup(cardItem.description)}
            />
          )}
          {cardItem.ctaLinkItem && cardItem.ctaLinkItem.length != 0 && (
            <div
              className={`flex flex-col items-${props.textAlignment.flex === 'around' ? 'start' : props.textAlignment.flex} gap-6 mt-10`}
            >
              {cardItem.ctaLinkItem.map((ctaLinkItem, i) => (
                <ButtonEl
                  key={i}
                  link={ctaLinkItem.link}
                  label={resolveCTALabel(cardItem, ctaLinkItem, isMember)}
                  theme={ctaLinkItem.linkType.toLowerCase()}
                  icon={ctaLinkItem.linkIconClass}
                  position={ctaLinkItem.linkPosition}
                  onClick={handleCardClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {cardItem.videoUrl ? (
        <Modal
          isOpen={modalIsOpen}
          onAfterOpen={afterOpenModal}
          onRequestClose={closeModal}
          className="modal-content--video"
          style={{ content: {} }}
          contentLabel="Example Modal"
          aria={{
            labelledby: `modal-heading-${i}`,
          }}
        >
          <ButtonEl className="right-5 absolute" onClick={closeModal} theme="none">
            <XMarkIcon role="none" className="w-6 h-6 shrink text-current" />
            <span className="sr-only">Close this alert</span>
          </ButtonEl>
          <div data-type="video-modal-content" className="m-0 relative w-full">
            {videoEmbed && (
              <div id={`modal-heading-${i}`} className="absolute -top-[1000px] opacity-0">
                {videoEmbed.title}
              </div>
            )}
            <Suspense fallback={<Loader />}>
              {videoEmbed && (
                <div
                  data-type="video-iframe-wrapper"
                  className="w-full h-full"
                  data-rte="true"
                  dangerouslySetInnerHTML={createMarkup(videoEmbed.html)}
                />
              )}
            </Suspense>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

const CardListIcon = ({ cardItem, i, cardBackgroundColour, backgroundColor, isDownloadable = false, isMember = null, onLockedClick }) => {
  const { createMarkup } = useContext(MarkupContext)
  const ctaLinkItems = cardItem.ctaLinkItem || []

  const badgeData = cardItem?.metaBadgeData || {}
  const firstCategoryTag = badgeData?.categoryTags?.[0]

  const isLocked = !!badgeData.isLocked
  const isVisuallyLocked = isLocked && isMember === false

  const handleLockedContentClick = (e) => {
    e.preventDefault()
    dataLayerPush({ event: 'login_intent', click_text: stripHtml(cardItem.title), member_only: true }, e.currentTarget)
    onLockedClick?.()
  }

  const handleCardClick = (e) => {
    if (isVisuallyLocked) {
      handleLockedContentClick(e)
      return
    }
    dataLayerPush({
      event: 'article_click',
      click_text: stripHtml(cardItem.title),
      category: badgeData.categoryTags?.length ? badgeData.categoryTags.join(', ') : undefined,
      member_only: false,
    }, e.currentTarget)
  }

  const handleQuickLinkClick = (e) => {
    dataLayerPush({
      event: 'quick_link_click',
      click_text: stripHtml(cardItem.title),
      destination_path: ctaLinkItems[0]?.link?.url,
      link_position: String(i + 1),
    }, e.currentTarget)
  }

  const handleDownloadClick = (e) => {
    e.cancelBubble = true
    e.preventDefault()
    e.stopPropagation()
    const fileUrl = ctaLinkItems[0]?.link?.url ?? null
    const fileName = fileUrl ? fileUrl.split('/').pop() : null
    dataLayerPush({
      event: 'file_download',
      destination_path: fileUrl,
      link_position: String(i + 1),
      file_name: fileName,
      content_type: ctaLinkItems[0]?.mediaFileExtensionLabel ?? null,
    }, e.currentTarget)
    setTimeout(() => {
      window.location.href = fileUrl
    }, 100)
  }


  const isSimpleCard = !hasContent(cardItem.description) && !firstCategoryTag

  const bgColor = normalizeHex(cardItem?.bgColor) || normalizeHex(cardBackgroundColour) || normalizeHex(backgroundColor)
  const lightOrDarkValue = bgColor ? lightOrDark(bgColor) : 'light'
  const textColor = lightOrDarkValue === 'dark' ? 'text-white' : 'text-black'

  const colStartClasses = `lg:nth-[3n+3]:col-start-9
                  lg:nth-[3n+2]:col-start-5
                  lg:nth-[3n+1]:col-start-1
                  lg:[&:last-child:first-child]:col-start-5
                  lg:[&:last-child:nth-child(3n+1)]:col-start-5
                  lg:[&:nth-last-child(2):first-child]:col-start-3
                  lg:[&:nth-last-child(2):nth-child(3n+1)]:col-start-3
                  lg:[&:nth-child(2):last-child]:col-start-7
                  lg:[&:last-child:nth-child(3n+2)]:col-start-7`

  // Downloadable cards size to their content on tablet+ (flex-wrap container),
  // full-width only on mobile for touch target size.
  const baseClasses = `relative h-full overflow-hidden ${isDownloadable ? 'w-full md:w-auto' : `w-full ${colStartClasses} max-md:col-span-full md:col-span-6 lg:col-span-4`} ${!isDownloadable ? 'rounded-e-3xl rounded-bl-3xl' : ''} p-6
    ${bgColor || isSimpleCard ? '' : 'border-1 border-(--color-border-tertiary)'}`

  const bgStyle = bgColor ? { backgroundColor: bgColor } : undefined

  if (isSimpleCard) {
    const singleCtaLinkItem = ctaLinkItems[0] || []
    const firstLink = singleCtaLinkItem?.link
    const simpleBgStyle = bgColor ? { backgroundColor: bgColor } : { backgroundColor: 'var(--sb-primary-green-bg)' }
    const mediaSizeLabel = isDownloadable
      ? [singleCtaLinkItem?.mediaFileExtensionLabel, singleCtaLinkItem?.mediaFileSizeFormatted].filter(Boolean).join(' ')
      : ''

    const content = (
      <div  className={`flex flex-row ${isDownloadable ? 'items-stretch' : 'items-center'} gap-10 w-full`}>
        {(cardItem.desktopImage || cardItem.mobileImage) && (
          <div className="w-16 h-16 shrink-0 flex items-center justify-center">
            <Picture
              desktopImage={cardItem.desktopImage}
              mobileImage={cardItem.mobileImage}
              className="aspect-square w-full h-full object-contain"
            />
          </div>
        )}
        {cardItem.title && (
          <div className={isDownloadable ? 'flex flex-col justify-center' : ''}>
            <CuratedCardTitle
              title={cardItem.title}
              useSmallTitle={cardItem.useSmallTitle}
              titleAlignment={cardItem.titleAlignment}
              className={`flex-1 min-w-0 break-words flex flex-col justify-center font-light xl:font-normal leading-tight [&>*]:!m-0 [&>*]:!p-0 !mb-0 !pb-0 ${textColor}`}
            />
            {isVisuallyLocked && (
              <div className={`flex items-center gap-1 mt-0.5 text-sm font-normal ${textColor}! opacity-80`}>
                <LockClosedIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                <span>Members only</span>
              </div>
            )}
            {isDownloadable && mediaSizeLabel && (
              <div className={`mt-0.5 text-base font-normal! ${textColor}`}>
                <span>{mediaSizeLabel}</span>
              </div>
            )}
          </div>
        )}
      </div>
    )

    if (firstLink?.url) {
      return (
        <a
          key={i}
          href={firstLink.url}
          target={firstLink.target ?? '_self'}
          style={{
          ...simpleBgStyle,
          '--text-color': lightOrDark(bgColor) === 'dark' ? 'var(--color-light)' : 'var(--color-dark)',
          }}
          data-bg={bgColor ? getSectionTheme(bgColor) : undefined}
          className={`flex text-(--text-color) items-center ${baseClasses} hover:scale-[1.02] transition-transform duration-200 ${isVisuallyLocked ? 'opacity-80' : ''}`}
          onClick={isVisuallyLocked ? handleLockedContentClick : isDownloadable ? handleDownloadClick : handleQuickLinkClick}
        >
          {content}
        </a>
      )
    }

    return (
      <div
        key={i}
        style={{
          ...simpleBgStyle,
          '--text-color': lightOrDark(bgColor) === 'dark' ? 'var(--color-light)' : 'var(--color-dark)',
          }}
        data-bg={bgColor ? lightOrDark(bgColor) === 'dark' ? 'dark' : 'light' : undefined}
        className={`flex text-(--text-color)  items-center ${baseClasses}`}
      >
        {content}
      </div>
    )
  }

  return (
    <div key={i} style={bgStyle} data-bg={bgColor ? getSectionTheme(bgColor) : undefined} className={baseClasses}>
      <div className={`relative grid items-start gap-4 xl:gap-8`}>
        <div className="w-16 row-start-1">
          <Picture
            desktopImage={cardItem.desktopImage}
            mobileImage={cardItem.mobileImage}
            className={`aspect-square w-full h-full object-cover`}
          />
        </div>

        <div className="row-start-2 pr-4">
          {(firstCategoryTag || badgeData?.readTime || badgeData?.publicationDate) && (
            <div className="flex flex-row items-center w-full mb-4">
              <div className="flex-1">
                {(firstCategoryTag || badgeData?.readTime) && (
                  <MetaBadge
                    primaryText={firstCategoryTag}
                    secondaryText={badgeData?.readTime}
                    isLocked={!!badgeData?.isLocked}
                    icon={badgeData?.isLocked ? <LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" /> : null}
                    tagType={!!badgeData?.isLocked ? 'locked_news_articles' : 'unlocked_news_articles'}
                  />
                )}
              </div>
              {badgeData?.publicationDate && (
                <span className="text-xs font-normal text-(--sb-common-base-body-text-color) shrink-0">
                  {badgeData?.publicationDate}
                </span>
              )}
            </div>
          )}
          {cardItem.title && (
            <HeroTitle type="curated" cardItem={cardItem} idx={i} className={'text-(--color-primary-text)'} onCardClick={handleCardClick} />
          )}
          {hasContent(cardItem.description) && (
            <div
              className="mt-6 xl:mt-8 !text-(--sb-common-base-body-text-color)"
              data-rte="true"
              dangerouslySetInnerHTML={createMarkup(cardItem.description)}
            />
          )}
        </div>
        {ctaLinkItems.length != 0 && (
          <div className="flex gap-6 pr-4">
            {ctaLinkItems.map((ctaLinkItem, i) => (
              <ButtonEl
                key={i}
                link={ctaLinkItem.link}
                label={resolveCTALabel(cardItem, ctaLinkItem, isMember)}
                theme={ctaLinkItem.linkType.toLowerCase()}
                icon={ctaLinkItem.linkIconClass}
                position={ctaLinkItem.linkPosition}
                className={`!text-sm !font-normal${ctaLinkItem.linkType.toLowerCase() === 'text' ? ' !text-(--color-primary-text)' : ''}`}
                onClick={handleCardClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const CardListHeroImage = (props) => {
  const { cardItem, i, isNews = false, isMember = null, onLockedClick, ...otherProps } = props

  const { createMarkup } = useContext(MarkupContext)
  const ctaLinkItems = cardItem.ctaLinkItem || []

  const badgeData = cardItem?.metaBadgeData || {}
  const firstCategoryTag = badgeData?.categoryTags?.[0]
  const isVisuallyLocked = !!badgeData.isLocked && isMember === false

  const handleCardClick = (e) => {
    if (isVisuallyLocked) {
      e.preventDefault()
      dataLayerPush({ event: 'login_intent', click_text: stripHtml(cardItem.title), member_only: true }, e.currentTarget)
      onLockedClick?.()
      return
    }
    dataLayerPush({
      event: 'article_click',
      click_text: stripHtml(cardItem.title),
      category: badgeData.categoryTags?.length ? badgeData.categoryTags.join(', ') : undefined,
      member_only: false,
    }, e.currentTarget)
  }

  const isStandardLayout = otherProps.layoutVariant === null || otherProps.layoutVariant === 'standard'

  const gridClasses = isStandardLayout
    ? `lg:nth-[3n+3]:col-start-9
       lg:nth-[3n+2]:col-start-5
       lg:nth-[3n+1]:col-start-1
       lg:[&:last-child:first-child]:col-start-5
       lg:[&:last-child:nth-child(3n+1)]:col-start-5
       lg:[&:nth-last-child(2):first-child]:col-start-3
       lg:[&:nth-last-child(2):nth-child(3n+1)]:col-start-3
       lg:[&:nth-child(2):last-child]:col-start-7
       lg:[&:last-child:nth-child(3n+2)]:col-start-7
       max-md:col-span-full md:col-span-6 lg:col-span-4`
    : `max-md:col-span-full ${i === 0 ? 'md:col-span-full lg:col-span-6' : i <= 2 ? 'md:col-span-6 lg:col-span-3' : 'md:col-span-6 lg:col-span-4'}`

  return (
    <div key={i} className={`relative w-full overflow-hidden ${gridClasses} p-6`}>
      <div className={`relative flex flex-col`}>
        {!isNews && (
          <Picture
            desktopImage={cardItem.desktopImage}
            mobileImage={cardItem.mobileImage}
            className={`mx-auto mb-8 aspect-video rounded-e-3xl rounded-bl-3xl`}
          />
        )}

        {(firstCategoryTag || badgeData?.readTime || badgeData?.publicationDate) && (
          <div className="flex flex-row items-center w-full mb-4">
            <div className="flex-1">
              {(firstCategoryTag || badgeData?.readTime) && (
                <MetaBadge
                  primaryText={firstCategoryTag}
                  secondaryText={badgeData?.readTime}
                  isLocked={!!badgeData?.isLocked}
                  icon={badgeData?.isLocked ? <LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" /> : null}
                  tagType={!!badgeData?.isLocked ? 'locked_news_articles' : 'unlocked_news_articles'}
                />
              )}
            </div>
            {badgeData?.publicationDate && (
              <span className="text-xs font-normal text-(--sb-common-base-body-text-color) shrink-0">
                {badgeData?.publicationDate}
              </span>
            )}
          </div>
        )}

        <div className="col-start-2 col-span-7 md:col-span-3 lg:col-span-5">
          {cardItem.title && (
            <HeroTitle type="curated" cardItem={cardItem} idx={i} className={'text-(--color-primary-text)'} onCardClick={handleCardClick} />
          )}
          {hasContent(cardItem.description) && (
            <div
              className="mt-4 !text-(--sb-common-base-body-text-color)"
              data-rte="true"
              dangerouslySetInnerHTML={createMarkup(cardItem.description)}
            />
          )}
        </div>
        {ctaLinkItems.length != 0 && (
          <div className="flex gap-6 mt-4">
            {ctaLinkItems.map((ctaLinkItem, i) => (
              <ButtonEl
                key={i}
                link={ctaLinkItem.link}
                label={resolveCTALabel(cardItem, ctaLinkItem, isMember)}
                theme={ctaLinkItem.linkType.toLowerCase()}
                icon={ctaLinkItem.linkIconClass}
                position={ctaLinkItem.linkPosition}
                className={`!text-sm !font-normal${ctaLinkItem.linkType.toLowerCase() === 'text' ? ' !text-(--color-primary-text)' : ''}`}
                onClick={handleCardClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function CardListCurated(props) {
  let cardItems = props.cardItems || []
  const initialNumber = props.initialNoOfCards || 3
  const [viewMore, setViewMore] = useState(cardItems.length <= initialNumber)
  const [variant, setVariant] = useState('')
  const [layoutVariant, setLayoutVariant] = useState('standard')
  const [textAlignment, setTextAlignment] = useState({
    flex: 'center',
    align: 'center',
  })
  const [isMember, setIsMember] = useState(null)
  const [showLockedBanner, setShowLockedBanner] = useState(false)
  const componentPadding = transformPaddingToTailwind(props.componentPadding)

  if (!viewMore) {
    cardItems = cardItems.slice(0, initialNumber)
  }

  useEffect(() => {
    setTextAlignment((prevState) => {
      const newState = { ...prevState }
      const alignment = props.contentAlignment?.toLowerCase() ?? 'center'
      if (newState.align !== props.contentAlignment?.toLowerCase()) {
        newState.flex = alignment === 'center' ? 'center' : 'around'
        newState.align = alignment === 'center' ? 'center' : 'left'
      }
      return newState
    })
    setVariant((prevState) => {
      if (!props.cardVariant) return 'background'
      return props.cardVariant?.split(' ')[0].toLowerCase()
    })

    setLayoutVariant((pre) => {
      if (!props.layoutVariant) return 'standard'
      return props.layoutVariant?.toLowerCase()
    })
  }, [])

  useEffect(() => {
    const hasLockedCards = (props.cardItems || []).some((c) => c.metaBadgeData?.isLocked)
    if (!hasLockedCards) return

    fetch('/api/content/member-status', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { isLoggedIn: false }))
      .then((data) => setIsMember(!!data.isLoggedIn))
      .catch(() => setIsMember(false))
  }, [])

  const ctaLinkItems = (props.ctaLinkItem || []).filter((ctaLinkItem) => ctaLinkItem.link?.url)
  return (
    <Section
      type="cardListCurated"
      innerClass={`px-6! lg:px-8! ${variant === 'downloadable' ? 'text-left' : `text-${textAlignment.align}`} ${componentPadding}`}
      headingClass="w-full"
      {...props}
    >
      <>
        {cardItems.length != 0 && (
          <>
            {layoutVariant === 'featured_list' ? (
              <FeaturedListCuratedCard
                props={props}
                cardItems={cardItems}
                variant={variant}
                textAlignment={textAlignment}
                isMember={isMember}
                onLockedClick={() => setShowLockedBanner(true)}
              />
            ) : (
              <div
                className={`${variant === 'downloadable' ? 'flex flex-wrap' : 'grid grid-cols-1 md:grid-cols-12'} ${hasContent(props.title) || hasContent(props.description) ? 'pt-10' : 'pt-0'} items-stretch gap-8`}
              >
                {cardItems.map((cardItem, i) => {
                  if (variant === 'background') {
                    return (
                      <CardListBGImage key={i} {...props} cardItem={cardItem} i={i} textAlignment={textAlignment} isMember={isMember} onLockedClick={() => setShowLockedBanner(true)} />
                    )
                  } else if (variant === 'hero' || variant === 'news') {
                    return (
                      <CardListHeroImage key={i} {...props} cardItem={cardItem} i={i} isNews={variant === 'news'} isMember={isMember} onLockedClick={() => setShowLockedBanner(true)} />
                    )
                  }
                  return (
                    <CardListIcon
                      key={i}
                      {...props}
                      cardItem={cardItem}
                      i={i}
                      isDownloadable={variant === 'downloadable'}
                      isMember={isMember}
                      onLockedClick={() => setShowLockedBanner(true)}
                    />
                  )
                })}
              </div>
            )}
          </>
        )}
        {!viewMore && (
          <div className="flex justify-center mt-20">
            <ButtonEl
              onClick={() => {
                setViewMore(true)
              }}
            >
              {props.viewMoreText || 'View more'}
              <ArrowRightIcon role="none" className="h-6 w-6 ml-2" />
            </ButtonEl>
          </div>
        )}
        <Modal
          isOpen={showLockedBanner}
          onRequestClose={() => setShowLockedBanner(false)}
          className="modal-content--locked-banner"
          style={{ content: {} }}
          contentLabel="Members only content"
          aria={{ labelledby: 'locked-banner-heading' }}
        >
          <div className="relative">
            <ButtonEl
              className="absolute right-4 top-4 z-10"
              onClick={() => setShowLockedBanner(false)}
              theme="none"
            >
              <XMarkIcon role="none" className="w-6 h-6 text-white" />
              <span className="sr-only">Close</span>
            </ButtonEl>
            <LockedContentBanner
              joinUrl={window?.pageData?.result?.lockedContentBannerSettings?.joinPageUrl || '/membership'}
              forgotPasswordUrl={window?.pageData?.result?.lockedContentBannerSettings?.forgotPasswordUrl}
              signInTitle={window?.pageData?.result?.lockedContentBannerSettings?.signInTitle}
              registrationPromoText={window?.pageData?.result?.lockedContentBannerSettings?.registrationPromoText}
              loginButtonText={window?.pageData?.result?.lockedContentBannerSettings?.loginButtonText}
              forgotPasswordText={window?.pageData?.result?.lockedContentBannerSettings?.forgotPasswordText}
              joinButtonLabel={window?.pageData?.result?.lockedContentBannerSettings?.joinButtonLabel}
            />
          </div>
        </Modal>
      </>
    </Section>
  )
}

export default CardListCurated
